import { z } from 'zod';

/**
 * Per-RevealTarget cursor visibility op family (06-timeline.md §13.2).
 *
 * The session cursor is rendered to players per a visibility mode stored as a
 * map from RevealTarget → VisibilityMode. This op family mirrors the reveal-op
 * family: a `cursor_visibility.set` op is HLC-stamped and folded into a
 * `Record<RevealTarget, VisibilityMode>` (the current effective map). The op
 * log IS the versioned audit log — "what was the Druid's visibility during
 * session 8?" is an op-log query; `config.set` is LWW with no history and
 * structurally cannot satisfy §13.2/§13.5.
 *
 * Keys are RevealTargets (`character` / `party` / `user`). The GM is always
 * `full` and is NOT a key here (omniscient over every cursor).
 *
 * Five modes — Full / Day-only / Season-only / Hidden / Spoofed:
 *   - `full`        — exact day shown; no granularity clamp.
 *   - `day_only`    — day granularity (same as full for the day axis).
 *   - `season_only` — clamped to season granularity via the calendar conversion.
 *   - `hidden`      — the cursor is ABSENT from the viewer's projection.
 *   - `spoofed`     — reserved for #254 (belief-layer synthetic composition);
 *                     included here so #254 plugs in without reshaping the enum.
 *
 * Restriction order (most → least restrictive):
 *   hidden > spoofed > season_only > day_only > full
 *
 * Resolved at the projection chokepoint by `visibilityForViewer` in
 * `packages/projection`.
 */

// ─── Mode + target types ────────────────────────────────────────────────────

export const VisibilityModeSchema = z.enum([
  'full',
  'day_only',
  'season_only',
  'hidden',
  'spoofed',
]);
export type VisibilityMode = z.infer<typeof VisibilityModeSchema>;

/**
 * Numeric restriction rank for each mode. Higher = more restrictive.
 * Shared by `visibilityForViewer` and any caller that needs to compare modes.
 */
export const VISIBILITY_MODE_RANK: Record<VisibilityMode, number> = {
  full: 0,
  day_only: 1,
  season_only: 2,
  spoofed: 3,
  hidden: 4,
};

/** The least-restrictive mode — the default when no explicit setting exists. */
export const DEFAULT_VISIBILITY_MODE: VisibilityMode = 'full';

/**
 * RevealTarget — the three kinds of non-GM Viewer a visibility mode can be
 * set on (matches `ViewerId.kind` minus `'gm'`).
 */
export const RevealTargetSchema = z.enum(['character', 'party', 'user']);
export type RevealTarget = z.infer<typeof RevealTargetSchema>;

// ─── Op payload ─────────────────────────────────────────────────────────────

/**
 * The reserved subject all `cursor_visibility.set` ops are written under.
 * Mirrors `CAMPAIGN_CONFIG_SUBJECT = '__campaign'` — the `__`-prefix keeps
 * these ops out of the user-addressable entity namespace; there is exactly
 * one cursor-visibility subject per Campaign.
 */
export const CURSOR_VISIBILITY_SUBJECT = '__cursor_visibility';

export const CursorVisibilitySetPayloadSchema = z
  .object({
    kind: z.literal('cursor_visibility.set'),
    reveal_target: RevealTargetSchema,
    mode: VisibilityModeSchema,
  })
  .strict();
export type CursorVisibilitySetPayload = z.infer<typeof CursorVisibilitySetPayloadSchema>;

// ─── Builder ─────────────────────────────────────────────────────────────────

/** Build a `cursor_visibility.set` op payload. */
export function cursorVisibilitySet(
  reveal_target: RevealTarget,
  mode: VisibilityMode,
): CursorVisibilitySetPayload {
  return { kind: 'cursor_visibility.set', reveal_target, mode };
}

// ─── Type guard ──────────────────────────────────────────────────────────────

/** Narrow an unknown op payload to `CursorVisibilitySetPayload` (shape-only check). */
export function isCursorVisibilityOp(payload: unknown): payload is CursorVisibilitySetPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'cursor_visibility.set'
  );
}

// ─── Fold ────────────────────────────────────────────────────────────────────

/**
 * Fold an iterable of op records into the per-RevealTarget visibility map.
 *
 * The ops MUST be in HLC order (as delivered by `iterateOps()`). The fold is
 * LWW per `reveal_target` — the last `cursor_visibility.set` op for a given
 * target wins. Non-cursor-visibility ops are skipped silently.
 *
 * Returns a plain `Record<RevealTarget, VisibilityMode>` (JSON-serialisable,
 * no JS Map — safe in llui Model state and replay snapshots).
 */
export function foldCursorVisibilityOps(
  ops: Iterable<{ payload: unknown }>,
): Partial<Record<RevealTarget, VisibilityMode>> {
  const out: Partial<Record<RevealTarget, VisibilityMode>> = {};
  for (const op of ops) {
    // Fully validate `reveal_target` and `mode`, not just the `kind`. A malformed
    // `mode` would otherwise land verbatim in the map; `visibilityForViewer` ranks
    // an unknown mode as `undefined`, silently degrading a restriction to the
    // least-restrictive default — a cursor-visibility LEAK. Drop invalid ops without
    // disturbing a previously-valid entry (LWW-defensive, matching foldConfigOps).
    const parsed = CursorVisibilitySetPayloadSchema.safeParse(op.payload);
    if (!parsed.success) continue;
    out[parsed.data.reveal_target] = parsed.data.mode;
  }
  return out;
}
